import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { BusinessService } from '@/api/generated/services/BusinessService'
import { BusinessResponse } from '@/api/generated/models/BusinessResponse'
import { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'

// Flexible business interface to support both full BusinessResponse and simplified objects
interface BusinessItem {
  id: string
  name: string
}

interface BusinessSelectProps {
  selectedBusinessId: string | null
  selectedBusinessName: string | null
  onSelect: (businessId: string, businessName: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  workspaceId?: string
  // External data support for enhanced performance and control
  businesses?: BusinessItem[]
  isLoading?: boolean
  businessesError?: string | null
  // Control whether to show "전체" option (for filtering vs form input)
  showAllOption?: boolean
}

// Custom hook for business data loading
const useBusinesses = (workspaceId: string) => {
  const [businesses, setBusinesses] = useState<BusinessItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBusinesses = useCallback(async () => {
    if (!workspaceId) return

    setIsLoading(true)
    setError(null)

    try {
      const response: PaginatedResponse = await BusinessService.appsBusinessViewsListBusinesses({
        workspaceId,
        page: 1,
        perPage: 100 // Load all businesses for dropdown
      })

      // Transform BusinessResponse to BusinessItem format
      const businessItems: BusinessItem[] = (response.data as BusinessResponse[]).map(business => ({
        id: business.id,
        name: business.name
      }))

      setBusinesses(businessItems)
    } catch (err) {
      console.error('Failed to fetch businesses:', err)
      setError('사업 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchBusinesses()
  }, [fetchBusinesses])

  return { businesses, isLoading, error, refetch: fetchBusinesses }
}

const BusinessSelect: React.FC<BusinessSelectProps> = ({
  selectedBusinessId,
  selectedBusinessName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = "사업을 선택하세요",
  workspaceId,
  // External data support
  businesses: externalBusinesses,
  isLoading: externalIsLoading,
  businessesError: externalBusinessesError,
  // Control "전체" option visibility (default: false for form inputs)
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Use external data if available, otherwise use internal hook (only if workspaceId is provided)
  const internalData = useBusinesses(externalBusinesses ? '' : (workspaceId || ''))
  
  const businesses = externalBusinesses || internalData.businesses
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalData.isLoading
  const fetchError = externalBusinessesError || internalData.error

  // Filter businesses based on search term (search business name only)
  const filteredBusinesses = useMemo(() => {
    if (!searchTerm.trim()) return businesses
    const searchLower = searchTerm.toLowerCase().trim()
    return businesses.filter(business =>
      business.name.toLowerCase().includes(searchLower)
    )
  }, [businesses, searchTerm])

  const handleSelect = (business: BusinessItem) => {
    onSelect(business.id, business.name)
    setIsOpen(false)
    setSearchTerm('') // Clear search when selecting
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '')
    setSearchTerm('') // Clear search when clearing selection
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('') // Clear search when opening dropdown
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedBusinessName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span>{selectedBusinessName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedBusinessId && selectedBusinessId.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown 
              className="w-5 h-5" 
              style={{ color: designTokens.colors.gray[3] }} 
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="사업명 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              사업 목록을 불러오는 중...
            </div>
          ) : fetchError ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.error
              }}
            >
              {fetchError}
            </div>
          ) : businesses.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              사업이 없습니다.
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              검색 결과가 없습니다.
            </div>
          ) : (
            <>
              {/* All Businesses Option - only show if showAllOption is true and no search term */}
              {showAllOption && !searchTerm.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect('', '')
                    setIsOpen(false)
                    setSearchTerm('')
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                >
                  전체
                </button>
              )}

              {filteredBusinesses.map((business) => (
                <button
                  key={business.id}
                  type="button"
                  onClick={() => handleSelect(business)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  <div>
                    <div 
                      className="font-medium"
                      style={{
                        fontSize: '16px',
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      {business.name}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div 
          className="mt-1 text-sm"
          style={{ 
            color: designTokens.colors.error,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default BusinessSelect